import React from 'react'
import './access-policy-view.scss'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader, Well } from '@pushly/aqe/lib/components'
import { noop } from '@pushly/aqe/lib/utils'
import { RouteComponentProps } from 'react-router-dom'
import { Breadcrumb, Button, Divider, Modal, Form, FormInstance, Input } from 'antd'
import { RuleObject } from 'antd/lib/form'
import { AppService } from '@halo/services/app.service'
import { useService } from '@pushly/aqe/lib/hooks'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'
import { IAccessPolicy } from '@halo/interfaces/access-policy'
import AccessPolicyService from '@halo/services/access-policy.service'
import FormLayoutRow from '@halo/components/form-layout-row/form-layout-row'
import { EditOutlined, ProfileOutlined, WarningOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import JsonView from '@halo/components/json-view/json-view'
import * as deepEqual from 'react-fast-compare'
import AccessPolicyEditor from './access-policy-editor'
import AccessPolicyUserRoleEditor from '@halo/components/access-policy-view/access-policy-user-role-editor'
import { simpleNotification } from '@halo/_utils/notifications'
import { isAxiosResponseError } from '@halo/_utils/axios'
import { IUserRole } from '@halo/interfaces/user-role'
import UserRoleService from '@halo/services/user-role.service'
// @ts-ignore
import { JsonEditor } from 'jsoneditor-react'
import 'jsoneditor-react/es/editor.min.css'

const ACCESS_CONTROL_ROUTE = '/access-control'
const ACCESS_POLICY_LIST_ROUTE = '/access-control/access-policies'
const REMOVE_KEY = 'REMOVE'

interface IViewProps extends RouteComponentProps<{ accessPolicyId: string }> {}

const AccessPolicyView: React.FunctionComponent<IViewProps> = (props) => {
    const appSvc = useService(AppService)
    const accessPolicySvc = useService(AccessPolicyService)
    const userRoleSvc = useService(UserRoleService)

    const accessPolicyId = parseInt(props.match.params.accessPolicyId, 10)

    const [_, runIfMounted] = useMountedRef()
    const [loading, setLoading] = React.useState(true)
    const [accessPolicy, setAccessPolicy] = React.useState<IAccessPolicy | null>(null)
    const [tmpConditionsValue, setTmpConditionsValue] = React.useState<any>({})
    const [detailsEditorVisible, setDetailsEditorVisible] = React.useState(false)
    const [conditionsEditorVisible, setConditionsEditorVisible] = React.useState(false)
    const [userRoles, setUserRoles] = React.useState<IUserRole[]>([])
    const [userRolesToRemove, setUserRolesToRemove] = React.useState<IUserRole[]>([])
    const [userRolesToAttach, setUserRolesToAttach] = React.useState<IUserRole[]>([])
    const [attachModalVisible, setAttachModalVisible] = React.useState(false)

    const jumpToAccessControl = React.useCallback((ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (ev.metaKey) {
            window.open(ev.currentTarget.href)
        } else {
            ev.preventDefault()
            appSvc.route(ACCESS_CONTROL_ROUTE)
        }
    }, [])

    const jumpToAccessPolicyList = React.useCallback((ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (ev.metaKey) {
            window.open(ev.currentTarget.href)
        } else {
            ev.preventDefault()
            appSvc.route(ACCESS_POLICY_LIST_ROUTE)
        }
    }, [])

    const loadUserRoles = React.useCallback(async () => {
        const { ok, data } = await userRoleSvc.fetchAll({
            showLoadingScreen: true,
            query: { pagination: 0 },
        })

        if (ok) {
            runIfMounted(() => {
                setUserRoles(data)
            })
        }
    }, [])

    const loadAccessPolicy = React.useCallback(async () => {
        const { ok, data } = await accessPolicySvc.fetch(accessPolicyId, {
            showLoadingScreen: true,
            query: { includeRoles: 1 },
        })

        if (ok) {
            runIfMounted(() => {
                setTmpConditionsValue(data.conditions ?? {})
                setAccessPolicy(data)
            })
        }
    }, [accessPolicyId])

    const loadDependencies = React.useCallback(async () => {
        await Promise.all([loadUserRoles(), loadAccessPolicy()])

        setLoading(false)
    }, [])

    const handleAttachModalClose = React.useCallback(() => {
        setAttachModalVisible(false)
        setUserRolesToAttach([])
    }, [])

    const closeConditionsEditor = React.useCallback(() => {
        setConditionsEditorVisible(false)
        setTmpConditionsValue(accessPolicy.conditions)
    }, [accessPolicy?.conditions])

    const handleAccessPolicyUpdate = React.useCallback(
        async (changes: Partial<IAccessPolicy>) => {
            const res = await accessPolicySvc.patch(accessPolicy.id, changes, {
                showLoadingScreen: true,
            })

            if (res.ok) {
                await loadDependencies()
                setDetailsEditorVisible(false)
                setConditionsEditorVisible(false)
            }
        },
        [accessPolicy],
    )

    const handleConditionsUpdate = React.useCallback(
        async (conditions: Partial<IAccessPolicy>) => {
            const res = await accessPolicySvc.patch(
                accessPolicy.id,
                { conditions },
                {
                    showLoadingScreen: true,
                },
            )

            if (res.ok) {
                await loadDependencies()
                closeConditionsEditor()
            } else if (isAxiosResponseError(res.error)) {
                simpleNotification('error', res.error.response.data.message ?? res.error.message)
            }
        },
        [accessPolicy],
    )

    const handleRemoveUserRoles = React.useCallback(async () => {
        const formRef = React.createRef<FormInstance>()

        Modal.confirm({
            title: 'Remove User Roles',
            icon: <WarningOutlined />,
            okText: 'Remove',
            onOk: async () => {
                const form = formRef.current

                if (form) {
                    await form.validateFields()
                    const res = await accessPolicySvc.removeUserRoleIds(
                        accessPolicy.id,
                        userRolesToRemove.map((r) => r.id),
                        {
                            showLoadingScreen: true,
                        },
                    )

                    if (res.ok) {
                        await loadDependencies()
                        setUserRolesToRemove([])
                    } else if (isAxiosResponseError(res.error)) {
                        simpleNotification('error', res.error.response.data.message ?? res.error.message)
                    }
                }
            },
            content: (
                <Form ref={formRef}>
                    <p>
                        Are you sure you want to remove the following user roles? Any users currently assigned these
                        roles will be affected.
                    </p>

                    <p>
                        Please type, <b>{REMOVE_KEY}</b>, to continue with this action.
                    </p>

                    <ul>
                        {userRolesToRemove.map((r) => (
                            <li key={r.id}>{r.description ?? r.name}</li>
                        ))}
                    </ul>

                    <Form.Item
                        name="name"
                        validateTrigger={false}
                        rules={[
                            {
                                validator: (_rule: RuleObject, value, callback) => {
                                    if (!value?.trim()) {
                                        callback(`${REMOVE_KEY} keyword is required`)
                                    } else if (value !== REMOVE_KEY) {
                                        callback(`You must type ${REMOVE_KEY} to continue`)
                                    } else {
                                        callback()
                                    }
                                },
                            },
                        ]}
                    >
                        <Input autoFocus={true} />
                    </Form.Item>
                </Form>
            ),
        })
    }, [accessPolicy, userRolesToRemove])

    const handleAttachUserRoles = React.useCallback(async () => {
        setAttachModalVisible(false)

        Modal.confirm({
            title: 'Attach User Roles',
            icon: <QuestionCircleOutlined />,
            okText: 'Attach',
            onOk: async () => {
                const res = await accessPolicySvc.attachUserRoleIds(
                    accessPolicy.id,
                    userRolesToAttach.map((r) => r.id),
                    {
                        showLoadingScreen: true,
                    },
                )

                if (res.ok) {
                    await loadDependencies()
                    setUserRolesToAttach([])
                } else if (isAxiosResponseError(res.error)) {
                    simpleNotification('error', res.error.response.data.message ?? res.error.message)
                }
            },
            onCancel: () => setAttachModalVisible(true),
            content: (
                <div>
                    <p>
                        Are you sure you want to attach the following user roles? Any users currently assigned these
                        roles will be affected.
                    </p>

                    <ul>
                        {userRolesToAttach.map((r) => (
                            <li key={r.id}>{r.description ?? r.name}</li>
                        ))}
                    </ul>
                </div>
            ),
        })
    }, [accessPolicy, userRolesToAttach])

    React.useEffect(() => {
        loadDependencies()
    }, [accessPolicyId])

    const hasConditions = Object.keys(accessPolicy?.conditions ?? {}).length > 0

    const currentUserRoleIds = (accessPolicy?.user_roles ?? []).map((ap) => ap.id)
    const currentUserRoles = userRoles.filter((role) => currentUserRoleIds.includes(role.id))
    const availableUserRoles = userRoles.filter((role) => !currentUserRoleIds.includes(role.id))

    return loading ? null : (
        <div className={classnames('page', 'access-policy')}>
            <div className={classnames('page-wrapper')}>
                <PageHeader
                    title="Access Policy"
                    append={
                        <Breadcrumb>
                            <Breadcrumb.Item href={ACCESS_CONTROL_ROUTE} onClick={jumpToAccessControl}>
                                Access Control
                            </Breadcrumb.Item>
                            <Breadcrumb.Item href={ACCESS_POLICY_LIST_ROUTE} onClick={jumpToAccessPolicyList}>
                                Access Policies
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>{accessPolicy.name}</Breadcrumb.Item>
                        </Breadcrumb>
                    }
                    onTitleSet={noop}
                />

                <Well
                    className="type-3"
                    title="Details"
                    hideFooter={true}
                    actions={[
                        <Button key="edit" size="small" onClick={() => setDetailsEditorVisible(true)}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>,
                    ]}
                >
                    <FormLayoutRow label="Name" value={accessPolicy.name} />
                    <FormLayoutRow label="Description" value={accessPolicy.description} />
                    <FormLayoutRow label="Enabled" value={accessPolicy.is_active ? 'TRUE' : 'FALSE'} />
                    <FormLayoutRow label="Created" value={moment(accessPolicy.created_at).format('LLLL')} />
                    <FormLayoutRow label="Created By" value={accessPolicy.created_by_user_name} />

                    <Divider />

                    <FormLayoutRow label="Effect" value={accessPolicy.effect} />
                    <FormLayoutRow label="Subjects" value={accessPolicy.subjects.join(', ')} />
                    <FormLayoutRow label="Actions" value={accessPolicy.actions.join(', ')} />
                </Well>

                <Well
                    className={classnames(null, 'type-3', 'access-policy-conditions-display', {
                        ['type-2']: conditionsEditorVisible,
                    })}
                    title="Conditions"
                    hideFooter={!conditionsEditorVisible}
                    actions={
                        !conditionsEditorVisible && [
                            <Button key="edit" size="small" onClick={() => setConditionsEditorVisible(true)}>
                                <EditOutlined />
                                <span>Edit</span>
                            </Button>,
                        ]
                    }
                    onCancel={closeConditionsEditor}
                    onSubmit={() => handleConditionsUpdate(tmpConditionsValue)}
                    disableSubmit={deepEqual(accessPolicy.conditions ?? {}, tmpConditionsValue)}
                >
                    {conditionsEditorVisible ? (
                        <JsonEditor
                            mode="code"
                            value={accessPolicy.conditions ?? {}}
                            onChange={setTmpConditionsValue}
                        />
                    ) : hasConditions ? (
                        <pre className="json-view-pretty">
                            <JsonView data={accessPolicy.conditions} hideActions={true} defaultExpanded={true} />
                        </pre>
                    ) : (
                        <AntdTableEmptyPlaceholder icon={<ProfileOutlined />} text="No Conditions" />
                    )}
                </Well>

                <AccessPolicyUserRoleEditor
                    onChange={(_roleIds, roles) => setUserRolesToRemove(roles)}
                    options={currentUserRoles}
                    value={userRolesToRemove.map((r) => r.id)}
                    actions={[
                        <Button
                            key="remove"
                            size="small"
                            disabled={!userRolesToRemove.length}
                            onClick={handleRemoveUserRoles}
                        >
                            <span>Remove</span>
                        </Button>,
                        <Button
                            key="add"
                            size="small"
                            type="primary"
                            onClick={() => {
                                setAttachModalVisible(true)
                                setUserRolesToRemove([])
                            }}
                        >
                            <span>Attach User Roles</span>
                        </Button>,
                    ]}
                />

                <Modal
                    className={classnames('attach-user-roles-modal')}
                    title={null}
                    footer={null}
                    visible={attachModalVisible}
                    destroyOnClose={true}
                    onCancel={handleAttachModalClose}
                >
                    <AccessPolicyUserRoleEditor
                        title="Attach User Roles"
                        onChange={(_roleIds, roles) => setUserRolesToAttach(roles)}
                        options={availableUserRoles}
                        value={userRolesToAttach.map((ap) => ap.id)}
                        actions={[
                            <Button key="cancel" size="small" onClick={handleAttachModalClose}>
                                <span>Cancel</span>
                            </Button>,
                            <Button
                                key="add"
                                size="small"
                                type={!userRolesToAttach.length ? null : 'primary'}
                                disabled={!userRolesToAttach.length}
                                onClick={handleAttachUserRoles}
                            >
                                <span>Attach</span>
                            </Button>,
                        ]}
                    />
                </Modal>

                <AccessPolicyEditor
                    accessPolicy={accessPolicy}
                    visible={detailsEditorVisible}
                    onClose={() => setDetailsEditorVisible(false)}
                    onSubmit={handleAccessPolicyUpdate}
                />
            </div>
        </div>
    )
}

export default AccessPolicyView
